import { useCallback, useMemo, useRef } from 'react';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { runPollInterval, statusForPendingRun, useRunStatusQuery } from './useRunStatusQuery';
import { startRunWithDeadline } from '../utils/runStartDeadline';
import { batchStatus, hasAllBatchRows } from '../components/BulkUpload/utils/batchStatus';
import {
  newIdempotencyKey,
  requestAssignmentFeedback,
} from '../service';
import {
  buildFeedbackRunPayload,
  failedStudents,
  failedSubmissionIds,
  firstReadySubmissionId,
  isRunActive,
  isRunAllFailed,
  isRunRecoverable,
  isRunSettled,
  runProgress,
  runStartedSince,
  runSummaryMessage,
} from '../components/BulkUpload/utils/feedbackRun';

export const FEEDBACK_RUN_POLL_MS = 2500;

export const statusPollInterval = (status, state = {}) =>
  runPollInterval(status, state, isRunActive, state.isRunRequested);

export const assignmentFeedbackRunKey = (assignmentId) => [
  'assignmentFeedbackRun',
  assignmentId,
];
export const assignmentFeedbackRequestKey = (assignmentId) => ['assignmentFeedbackRequest', assignmentId];

/**
 * Whether the rows say this attempt took. Latched on the attempt: rows only move
 * forward, and a row seen moving has started however its settled state happens
 * to compare with the reading before the request.
 */
const attemptStarted = (attempt, status) => {
  if (!attempt) return false;
  if (!attempt.started && runStartedSince(
    batchStatus(status, attempt.submissionIds), attempt.baseline, attempt.observed,
  )) {
    attempt.started = true;
  }
  return attempt.started;
};

/**
 * The read half, on its own: one request, and a poll only while something is
 * actually moving.
 *
 * Every caller shares this query key, so the header's student switcher and the
 * open document subscribe to ONE timer rather than starting one each. A teacher
 * opening a long-finished assignment makes one request and no poll at all —
 * NOT_REQUESTED is neither settled nor in flight, so nothing schedules a tick.
 */
export const useAssignmentSubmissionsStatus = (
  assignmentId,
  { enabled = true, isRunRequested = false } = {},
) => useRunStatusQuery({
  assignmentId, queryKey: assignmentFeedbackRunKey(assignmentId), enabled,
  isRunRequested, isActive: isRunActive,
});

/**
 * One POST for the whole assignment, then one poll that stops on its own.
 *
 * The fan-out this replaces lived in the browser: N requests, N waits for OCR
 * text the server already had, and nothing server-side remembering the intent —
 * so closing the tab mid-flight left the rest of the class unmarked.
 *
 * The poll runs on isRunActive, not on !isRunSettled: a submission nobody asked
 * for feedback on reports NOT_REQUESTED forever, and watching it would put a
 * timer on the page that never ends. So a teacher opening a finished assignment
 * makes one request and no poll at all — and the one thing that reads
 * NOT_REQUESTED and IS worth watching, a run this hook has just asked for, says
 * so through isRunRequested rather than by loosening the rule for everyone.
 *
 * The status endpoint is the source of truth, not this hook's memory, so a
 * refresh mid-run resumes the progress view rather than showing an empty wizard.
 *
 * It outranks the accept for the same reason. The accept is one long POST that
 * anything in front of the handler can give up on while the server keeps marking
 * (JED-1387: Cloud Run cut a 338s bulk accept at 300s and the teacher was told
 * the feedback could not be requested, under five ticked steps). A rejected
 * accept is read against the rows before it is called a refusal: a run the rows
 * show moving is on its way, receipt or no receipt.
 */
export const useAssignmentFeedbackRun = (assignmentId, { enabled = true, submissionIds, watchWholeBatch = false } = {}) => {
  const client = useQueryClient();
  const requestScope = useRef(null);
  if (!requestScope.current || requestScope.current.assignmentId !== assignmentId) {
    requestScope.current = { assignmentId, key: null, attempt: null };
  }
  const scope = requestScope.current;
  const requestsInFlight = useIsMutating({ mutationKey: assignmentFeedbackRequestKey(assignmentId) }) > 0;
  const requestMutation = useMutation({
    mutationKey: assignmentFeedbackRequestKey(assignmentId),
    mutationFn: ({ target, payload, key }) => startRunWithDeadline(signal => requestAssignmentFeedback(target, payload, key, { signal })),
    retry: false,
    networkMode: 'always',
    onSuccess: async (_receipt, { owner }) => {
      if (owner !== requestScope.current) return;
      await statusQuery.refreshAfterStart();
      owner.attempt.awaitingRead = false;
    },
    // Read the rows before the rejection reaches anyone, so the decision is made
    // on what the server is doing now rather than on what it said last. The
    // library awaits this before the mutation rejects.
    onError: async (_error, { owner }) => {
      if (owner !== requestScope.current) return;
      const fresh = await statusQuery.refreshAfterStart()
        .then((result) => result?.data, () => undefined);
      const latest = fresh ?? client.getQueryData(assignmentFeedbackRunKey(assignmentId));
      // That read was the post-accept read; nothing else is coming to unmask the rows.
      if (attemptStarted(owner.attempt, latest)) owner.attempt.awaitingRead = false;
    },
  });
  const isRequesting = requestsInFlight || (requestMutation.variables?.owner === scope && requestMutation.isLoading);
  const rejection = requestMutation.variables?.owner === scope ? requestMutation.error : null;
  const statusQuery = useAssignmentSubmissionsStatus(assignmentId, { enabled, isRunRequested: isRequesting });
  const { data: assignmentStatus, error: statusError, refetch: refetchStatus } = statusQuery;
  const attempt = scope.attempt;
  const targetedIds = watchWholeBatch ? submissionIds : attempt?.submissionIds ?? submissionIds;
  // A rejected accept is a refusal only while the rows say nothing happened.
  // Not memoised: the latch can flip between renders without a new status read.
  const requestError = rejection && !attemptStarted(attempt, assignmentStatus) ? rejection : null;
  const status = useMemo(() => {
    if (!attempt?.awaitingRead) return batchStatus(assignmentStatus, targetedIds);
    const pending = statusForPendingRun(batchStatus(assignmentStatus, attempt.submissionIds),
      batchStatus(attempt.baseline, attempt.submissionIds), 'feedback', attempt.observed);
    const byId = new Map((pending?.submissions || []).map(row => [row.submissionId || row.id, row]));
    return batchStatus(assignmentStatus && { ...assignmentStatus, submissions: assignmentStatus.submissions?.map(
      row => byId.get(row.submissionId || row.id) || row,
    ) }, targetedIds);
  }, [assignmentStatus, targetedIds, attempt, isRequesting]);

  const request = useCallback((selection, controls, options = {}) => {
    if (!assignmentId) return Promise.reject(new Error('Feedback cannot start before the task is saved.'));
    if (scope.promise) return scope.promise;
    if (options.newRun || !scope.key) scope.key = newIdempotencyKey();
    const attempt = {
      baseline: client.getQueryData(assignmentFeedbackRunKey(assignmentId)) ?? assignmentStatus,
      submissionIds: options.submissionIds ?? submissionIds,
      awaitingRead: true, observed: new Map(), started: false,
    };
    scope.attempt = attempt;
    const payload = buildFeedbackRunPayload(selection, controls, { ...options, submissionIds: attempt.submissionIds });
    const pending = requestMutation.mutateAsync({ target: assignmentId, payload, key: scope.key, owner: scope })
      // No receipt, but the rows show the run took (onError has read them by
      // now): the run is on its way, which is all a caller asked for.
      .catch((error) => {
        if (attempt.started) return { unconfirmed: true, error };
        throw error;
      });
    scope.promise = pending;
    const clear = () => { if (scope.promise === pending) scope.promise = null; };
    pending.then(clear, clear);
    return pending;
  }, [scope, assignmentId, assignmentStatus, submissionIds, requestMutation]);

  const derived = useMemo(
    () => ({
      // The accept was rejected after the rows had already moved: a start the
      // server confirmed by doing it rather than by answering.
      isStartUnconfirmed: Boolean(rejection) && !requestError,
      progress: runProgress(status),
      message: runSummaryMessage(status),
      failures: failedStudents(status),
      // What a retry would ask for, and whether asking is worth offering.
      failedSubmissionIds: failedSubmissionIds(status),
      isRecoverable: !isRequesting && !statusError && isRunRecoverable(status),
      firstReadySubmissionId: statusError || requestError ? null : firstReadySubmissionId(status),
      isSettled: !isRequesting && !attempt?.awaitingRead && !statusError && hasAllBatchRows(status, targetedIds) && isRunSettled(status),
      // Newly completed rows can be opened while the inline accept is still returning.
      // Unchanged terminal rows were masked above and cannot satisfy this gate.
      isReadyForHandoff: !statusError && !requestError && hasAllBatchRows(status, targetedIds) && isRunSettled(status) && !isRunRecoverable(status),
      isActive: isRunActive(status),
      isAllFailed: !isRequesting && !attempt?.awaitingRead && !statusError && isRunAllFailed(status),
    }),
    [status, targetedIds, isRequesting, statusError, requestError, rejection, attempt],
  );

  return {
    request,
    refetchStatus,
    status,
    statusError,
    requestError,
    isRequesting,
    ...derived,
  };
};
